import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Button, Col, Input, Row } from 'antd';

import styles from '../styles';
import { errorNotification, successNotification } from '../Snackbar';
import Api from '../Api';
import Breadcrumbs from '../Components/Breadcrumbs';
import GetField from '../Components/GetField';
import { clearSampleItemsToIssue, getSampleItemsToIssue } from '../Actions';

const AntInput = GetField(Input);

const SampleIssueForm = props => {
  const { classes, match } = props;

  const dispatch = useDispatch();
  const history = useHistory();

  const sampleItemsToIssue = useSelector(state => state.sampleItemsToIssue);

  const [confirmLoading, setConfirmLoading] = useState(false);

  const { handleSubmit, control, setValue } = useForm();

  const { fields: fabricFields } = useFieldArray({
    control,
    name: 'fabrics',
  });

  const { fields: accessoryFields } = useFieldArray({
    control,
    name: 'accessories',
  });

  useEffect(() => {
    dispatch(getSampleItemsToIssue(match.params.id));

    return () => {
      dispatch(clearSampleItemsToIssue());
    };
  }, []);

  useEffect(() => {
    setValue('fabrics', sampleItemsToIssue.fabrics);
  }, [sampleItemsToIssue.fabrics]);

  useEffect(() => {
    setValue('accessories', sampleItemsToIssue.accessories);
  }, [sampleItemsToIssue.accessories]);

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.post(`/samples/issueitems/${match.params.id}`, formValues);

      successNotification('Sample items issued');
      history.push(`/samples/preview/${match.params.id}`);
    } catch (error) {
      setConfirmLoading(false);
      errorNotification(error);
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Sample"
        moduleLink="/samples/List"
        subModuleName="List"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        {fabricFields.length ? (
          <>
            <div className={classes.previewSubHeading}>Fabrics Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh}>Product</th>
                      <th className={classes.inputTh}>Fabric</th>
                      <th className={classes.inputTh}>Width</th>
                      <th className={classes.inputTh}>Base Color</th>
                      <th className={classes.inputTh}>Print Color</th>
                      <th className={classes.inputTh}>Factory</th>
                      <th className={classes.inputTh}>Rack #</th>
                      <th className={classes.inputTh}>Sample Qty</th>
                      <th className={classes.inputTh}>Stock</th>
                      <th className={classes.inputTh}>Issue Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fabricFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.product}</td>
                        <td className={classes.inputTd}>{item.fabric}</td>
                        <td className={classes.inputTd}>{item.width}</td>
                        <td className={classes.inputTd}>{item.baseColor}</td>
                        <td className={classes.inputTd}>{item.printColor}</td>
                        <td className={classes.inputTd}>{item.factory}</td>
                        <td className={classes.inputTd}>{item.rackno}</td>
                        <td className={classes.inputTd}>
                          {item.sampleQuantity}
                        </td>
                        <td className={classes.inputTd}>{item.inventory}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                size="small"
                                type="number"
                                min={0}
                                placeholder="Issue Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        {accessoryFields.length ? (
          <>
            <div className={classes.previewSubHeading}>Accessories Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh}>Product</th>
                      <th className={classes.inputTh}>Accessory</th>
                      <th className={classes.inputTh}>Factory</th>
                      <th className={classes.inputTh}>Rack #</th>
                      <th className={classes.inputTh}>Sample Qty</th>
                      <th className={classes.inputTh}>Stock</th>
                      <th className={classes.inputTh}>Issue Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessoryFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.product}</td>
                        <td className={classes.inputTd}>{item.accessory}</td>
                        <td className={classes.inputTd}>{item.factory}</td>
                        <td className={classes.inputTd}>{item.rackno}</td>
                        <td className={classes.inputTd}>
                          {item.sampleQuantity}
                        </td>
                        <td className={classes.inputTd}>{item.inventory}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                size="small"
                                type="number"
                                min={0}
                                placeholder="Issue Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        {fabricFields.length || accessoryFields.length ? (
          <Row className={classes.buttonContainer}>
            <Button type="primary" loading={confirmLoading} htmlType="submit">
              Submit
            </Button>
          </Row>
        ) : (
          <div>There is no stock to transfer</div>
        )}
      </form>
    </>
  );
};

SampleIssueForm.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(SampleIssueForm);
